/********************************************************* {COPYRIGHT-TOP} ***
 * Licensed Materials - Property of IBM
 *
 * (C) Copyright IBM Corporation 2020, 2024
 *
 * All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 ********************************************************** {COPYRIGHT-END} **/

import ApiCommon from './api-common';
import TranslatableAsyncObject from 'utils/TranslatableAsyncObject';
import { isEmpty, get, isNil, uniqBy } from 'lodash';
import { removeParserKeys } from 'utils/common';

const SUPPORTED_VERSIONS = ['2.0.0', '2.1.0'];

const SCHEMA_FORMAT_MATCH = new RegExp('^(.*);version=([0-9.]+|draft-[0-9]+)');

const AVRO_SCHEMA = 'Avro';
const ASYNCAPI_SCHEMA = 'AsyncAPI';
const OPENAPI_SCHEMA = 'OpenAPI';
const JSON_SCHEMA = 'JSON';

const SUPPORTED_SCHEMA_FORMATS = {
  'application/vnd.aai.asyncapi': ASYNCAPI_SCHEMA,
  'application/vnd.aai.asyncapi+json': ASYNCAPI_SCHEMA,
  'application/vnd.aai.asyncapi+yaml': ASYNCAPI_SCHEMA,
  'application/vnd.oai.openapi': OPENAPI_SCHEMA,
  'application/vnd.oai.openapi+json': OPENAPI_SCHEMA,
  'application/vnd.oai.openapi+yaml': OPENAPI_SCHEMA,
  'application/schema+json': JSON_SCHEMA,
  'application/schema+yaml': JSON_SCHEMA,
  'application/vnd.apache.avro': AVRO_SCHEMA,
  'application/vnd.apache.avro+json': AVRO_SCHEMA,
  'application/vnd.apache.avro+yaml': AVRO_SCHEMA
};

const NON_PRIMITIVE_TYPES = ['object', 'record'];

const getReadableSchemaFormat = (schemaFormat) => {
  const typeAndVersion = getSchemaTypeAndVersion(schemaFormat);
  if (typeAndVersion && typeAndVersion.type) {
    typeAndVersion.type = SUPPORTED_SCHEMA_FORMATS[typeAndVersion.type];
  }
  return typeAndVersion;
};

const getSchemaTypeAndVersion = (schemaFormat) => {
  let result = { type: null, version: null };
  if (schemaFormat) {
    const formatMatch = schemaFormat.match(SCHEMA_FORMAT_MATCH);
    if (formatMatch && formatMatch.length > 2) {
      result.type = formatMatch[1];
      result.version = formatMatch[2];
    }
  }
  return result;
};

const isSupportedByAsyncAPIV2 = (api) => {
  if (api && api.version) {
    return SUPPORTED_VERSIONS.includes(api.version());
  } else {
    return false;
  }
};

const hasMessages = (operation) => {
  return operation.messages().length > 0;
};

export default class AsyncApiV2 extends ApiCommon {
  channelHasSubscribe(channelName) {
    const channel = this.api.channel(channelName);
    return channel ? channel.hasSubscribe() : false;
  }

  channelHasPublish(channelName) {
    const channel = this.api.channel(channelName);
    return channel ? channel.hasPublish() : false;
  }

  /**
   * checks that the given file is a supported AsyncAPI version
   */
  isSupported() {
    return isSupportedByAsyncAPIV2(this.api);
  }

  /**
   * @returns the endpoint urls and endpoint display labels as an array
   */
  getEndpoints() {
    let endpoints = [];

    if (this.api.serverNames().length > 0) {
      const protocol = this.getFirstProtocol();
      endpoints = Object.entries(this.api.servers())
        .filter(([_, server]) => server.protocol() === protocol)
        .map(([name, server]) => ({ url: server.url(), types: name, bindings: server.bindings() }));
    }

    return endpoints;
  }

  /**
   * @returns all schemas in the api
   */
  getSchemas() {
    return {};
  }

  isAsyncApiSchema(schemaType) {
    return schemaType === ASYNCAPI_SCHEMA;
  }

  isAvroSchema(schemaType) {
    return schemaType === AVRO_SCHEMA;
  }

  isJSONSchema(schemaType) {
    return schemaType === JSON_SCHEMA;
  }

  isOpenApiSchema(schemaType) {
    return schemaType === OPENAPI_SCHEMA;
  }

  getFirstMessageHeader(channelName) {
    const channel = this.api.channel(channelName);
    let [schema, example] = [null, null];

    if (channel && channel.hasSubscribe() && hasMessages(channel.subscribe())) {
      const firstMessage = channel.subscribe().messages()[0];
      if (firstMessage.headers()) {
        schema = removeParserKeys(firstMessage.headers().json());
        const examples = channel.subscribe().messages()[0].examples();
        if (examples && examples.length > 0 && examples[0].headers) {
          example = examples[0].headers;
        }
      }
    } else if (channel && channel.hasPublish() && hasMessages(channel.publish())) {
      const firstMessage = channel.publish().messages()[0];
      if (firstMessage.headers()) {
        schema = removeParserKeys(firstMessage.headers().json());
        const examples = channel.publish().messages()[0].examples();
        if (examples && examples.length > 0 && examples[0].headers) {
          example = examples[0].headers;
        }
      }
    }

    return {
      schema,
      example
    };
  }

  /**
   * @returns first schema from a channel
   */
  getFirstSchema(channelName) {
    const channel = this.api.channel(channelName);

    if (channel && channel.hasSubscribe() && hasMessages(channel.subscribe())) {
      let payload = channel.subscribe().messages()[0].originalPayload();
      let format = channel.subscribe().messages()[0].originalSchemaFormat();
      let examples = channel.subscribe().messages()[0].examples();
      let encodingType = channel.subscribe().messages()[0].contentType();
      let example = null;

      if (typeof payload === 'string') {
        payload = {
          type: payload
        };
      }

      if (examples && examples.length > 0 && examples[0].payload) {
        example = examples[0].payload;
      }

      const schemaTypeAndVersion = getReadableSchemaFormat(format);
      return { payload, format, example, type: schemaTypeAndVersion.type, version: schemaTypeAndVersion.version, encodingType };
    } else if (channel && channel.hasPublish() && hasMessages(channel.publish())) {
      let payload = channel.publish().messages()[0].originalPayload();
      let format = channel.publish().messages()[0].originalSchemaFormat();
      let examples = channel.publish().messages()[0].examples();
      let encodingType = channel.publish().messages()[0].contentType();
      let example = null;

      if (typeof payload === 'string') {
        payload = {
          type: payload
        };
      }

      if (examples && examples.length > 0 && examples[0].payload) {
        example = examples[0].payload;
      }

      const schemaTypeAndVersion = getReadableSchemaFormat(format);
      return { payload, format, example, type: schemaTypeAndVersion.type, version: schemaTypeAndVersion.version, encodingType };
    } else {
      return { payload: null, format: null, example: null, type: null, version: null, encodingType: null };
    }
  }

  /**
   * @returns first message from a channel
   */
  getFirstMessage(channelName) {
    const channel = this.api.channel(channelName);

    if (channel.hasSubscribe() && hasMessages(channel.subscribe())) {
      const translatableMessage = new TranslatableAsyncObject(channel.subscribe().messages()[0]);
      const bindings = channel.subscribe().messages()[0].bindings();
      return { description: translatableMessage.description(), summary: translatableMessage.summary(), title: translatableMessage.title(), bindings: bindings };
    } else if (channel.hasPublish() && hasMessages(channel.publish())) {
      const translatableMessage = new TranslatableAsyncObject(channel.publish().messages()[0]);
      const bindings = channel.publish().messages()[0].bindings();
      return { description: translatableMessage.description(), summary: translatableMessage.summary(), title: translatableMessage.title(), bindings: bindings };
    } else {
      return {};
    }
  }

  /**
   * @returns all channels definined in the api
   */
  getChannelNames() {
    let channels = [];

    if (this.api.hasChannels()) {
      channels = this.api.channelNames();
    }

    return channels;
  }

  /**
   * @returns first protocol defined in the api servers
   */
  getFirstProtocol() {
    let protocol = '';
    if (this.api.serverNames().length > 0) {
      protocol = Object.values(this.api.servers())[0].protocol();
    }

    return protocol;
  }

  getFirstSecurityType() {
    if (this.api.serverNames().length > 0) {
      const securityList = Object.values(this.api.servers())[0].security();
      if (securityList && this.api.hasComponents() && this.api.components().hasSecuritySchemes()) {
        const firstSecurity = Object.keys(securityList[0].json())[0];
        const securityScheme = this.api.components().securitySchemes()[firstSecurity];

        return {
          type: securityScheme.type(),
          description: new TranslatableAsyncObject(securityScheme).description()
        };
      }
    }

    return {
      type: null,
      description: null
    };
  }

  getChannel(channelName) {
    return this.api.channel(channelName);
  }

  getChannelBindings(channelName) {
    const channel = this.getChannel(channelName);
    if (channel && channel.hasBindings()) {
      return channel.bindings();
    }
    return null;
  }

  getSubscribeBindings(channelName) {
    const channel = this.getChannel(channelName);
    if (channel && channel.hasSubscribe() && channel.subscribe().hasBindings()) {
      return channel.subscribe().bindings();
    }
    return null;
  }

  getPublishBindings(channelName) {
    const channel = this.getChannel(channelName);
    if (channel && channel.hasPublish() && channel.publish().hasBindings()) {
      return channel.publish().bindings();
    }
    return null;
  }

  getMessage(channelName, operation) {
    const channel = this.getChannel(channelName);
    switch (operation) {
      case 'publish':
        if (channel && channel.hasPublish() && channel.publish().messages().length > 0) {
          return channel.publish().messages()[0];
        }
        break;
      default:
      case 'subscribe':
        if (channel && channel.hasSubscribe() && channel.subscribe().messages().length > 0) {
          return channel.subscribe().messages()[0];
        }
    }
    return null;
  }

  getMessageBindings(message) {
    if (message && message.hasBindings()) {
      return message.bindings();
    }
    return null;
  }

  getDescription(channelName) {
    const channel = this.getChannel(channelName);
    if (channel && channel.hasDescription()) {
      return new TranslatableAsyncObject(channel).description();
    }
    return null;
  }

  getParameters(channelName) {
    const channel = this.getChannel(channelName);
    if (channel && channel._json && channel._json.parameters) {
      let parameters = [];
      for (let param in channel._json.parameters) {
        parameters.push({
          name: param,
          description: channel._json.parameters[param].description || null,
          schema: channel._json.parameters[param].schema || null,
          location: channel._json.parameters[param].location || null
        });
      }
      return parameters;
    }
    return [];
  }

  getSubscribe(channelName) {
    const channel = this.getChannel(channelName);
    if (channel && channel.hasSubscribe()) {
      const translatableSubscribe = new TranslatableAsyncObject(channel.subscribe());
      return {
        summary: translatableSubscribe.summary() || null,
        description: translatableSubscribe.description() || null
      };
    }
    return null;
  }

  getPublish(channelName) {
    const channel = this.getChannel(channelName);
    if (channel && channel.publish()) {
      const translatablePublish = new TranslatableAsyncObject(channel.publish());
      return {
        summary: translatablePublish.summary() || null,
        description: translatablePublish.description() || null
      };
    }
    return null;
  }

  isPrimitiveSchema(payload) {
    if (isNil(payload) || (payload && !isEmpty(payload) && (typeof payload === 'string' || !NON_PRIMITIVE_TYPES.includes(payload.type)))) {
      return true;
    }
    return false;
  }

  getPrimitiveSchemaInfo(payload) {
    const primitivePayloadInformation = {};
    if (payload && typeof payload === 'string') {
      primitivePayloadInformation.type = payload;
    } else if (payload && payload.type) {
      primitivePayloadInformation.type = payload.type;
      if (payload.format) primitivePayloadInformation.format = payload.format;
    }

    return primitivePayloadInformation;
  }

  getServerBindings(serverName) {
    const servers = this.api.servers();
    const server = servers[serverName];
    if (server && server.hasBindings()) {
      return server.bindings();
    }
    return null;
  }

  getSecurityDefinitions() {
    let result = {};
    if (this.api.components() && this.api.components().securitySchemes()) {
      Object.entries(this.api.components().securitySchemes()).forEach(([key, scheme]) => {
        result[key] = scheme.json();
      });
    }
    return result;
  }

  getSecurity() {
    const servers = Object.values(this.api.servers());

    const allServerSecurity = servers.flatMap((server) => {
      if (server.security()) {
        return server.security().reduce((acc, security) => {
          acc = [...acc, security.json()];
          return acc;
        }, []);
      } else {
        return [];
      }
    });
    return uniqBy(allServerSecurity, (securityRequirement) => Object.keys(securityRequirement)[0]);
  }

  isEnforced() {
    return get(this.api._json, 'x-ibm-configuration.enforced', false);
  }
}

export { isSupportedByAsyncAPIV2, AsyncApiV2 };
